import { nativePageParams } from '~/nativeLenses/pageRoutes';

import { formatDateISO } from '~/services/TimeService';

import { confidenceSelectionParam, normalizeConfidenceSelection } from './confidenceSelection';
import type { ChartUnit, CommandFilters, PeriodPoint } from './logic';
import type { ConfidenceTier, CostBasis } from './types';

export type SelectedToursPeriod = Pick<PeriodPoint, 'key' | 'from' | 'to'>;
export type ToursAnalysisState = {
  unit: ChartUnit;
  period: SelectedToursPeriod | null;
  priorRange: Pick<CommandFilters, 'dateFrom' | 'dateTo'> | null;
};
const validDay = (value: unknown): value is string =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) && formatDateISO(value) === value;

export type DrillFilters = {
  dateFrom: string | null;
  dateTo: string | null;
  confidenceTier: ConfidenceTier[];
  basis: CostBasis;
};

/** Drill choices use separate keys so the overview URL remains intact. */
export function readDrillFilters(search: string, overview: CommandFilters): DrillFilters {
  const params = new URLSearchParams(search);
  const confidence = params.get('drillConfidence');
  const from = params.get('drillFrom');
  const to = params.get('drillTo');
  return {
    dateFrom: validDay(from) ? from : (overview.dateFrom ?? null),
    dateTo: validDay(to) ? to : (overview.dateTo ?? null),
    confidenceTier: normalizeConfidenceSelection(confidence === null ? overview.confidenceTier : confidence),
    basis: params.get('drillBasis') === 'rep' ? 'rep' : params.get('drillBasis') === 'ss' ? 'ss' : overview.basis,
  };
}

export function writeDrillFilters(search: string, filters: DrillFilters): string {
  const params = new URLSearchParams(search);
  params.set('drillFrom', filters.dateFrom ?? '');
  params.set('drillTo', filters.dateTo ?? '');
  params.set('drillConfidence', confidenceSelectionParam(filters.confidenceTier));
  params.set('drillBasis', filters.basis);
  return `?${params}`;
}

export function overviewSearch(search: string): string {
  const params = new URLSearchParams(search);
  for (const key of ['drillFrom', 'drillTo', 'drillConfidence', 'drillBasis']) params.delete(key);
  return `?${params}`;
}

export function readToursAnalysis(search: string): ToursAnalysisState {
  const params = new URLSearchParams(search);
  const unit = params.get('tccUnit');
  const result: ToursAnalysisState = {
    unit: unit === 'week' || unit === 'departure' ? unit : 'month',
    period: null,
    priorRange: null,
  };
  try {
    const value: unknown = JSON.parse(params.get('tccPeriod') ?? 'null');
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return result;
    }
    const point = value as Record<string, unknown>;
    if (
      point.version !== 1 ||
      !validDay(point.key) ||
      !validDay(point.from) ||
      !validDay(point.to) ||
      point.from > point.to
    ) {
      return result;
    }
    if (
      (point.priorFrom != null && !validDay(point.priorFrom)) ||
      (point.priorTo != null && !validDay(point.priorTo))
    ) {
      return result;
    }
    result.period = { key: point.key, from: point.from, to: point.to };
    result.priorRange = { dateFrom: (point.priorFrom as string) ?? null, dateTo: (point.priorTo as string) ?? null };
  } catch {
    // Hand-edited selections leave the main date filters and chart usable.
  }
  return result;
}

/** Persist viewer choices, leaving omitted dates relative to the reporting snapshot. */
export function toursSearch(
  search: string,
  filters: CommandFilters,
  attentionFlag: string | null,
  analysis: ToursAnalysisState,
): string {
  const params = nativePageParams(search);
  for (const [key, value] of [
    ['from', filters.dateFrom],
    ['to', filters.dateTo],
    ['tour', filters.tourId],
    ['severity', attentionFlag],
  ]) {
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
  }
  params.delete('band');
  params.delete('ltle');
  params.set('action', filters.action || 'all');
  params.set('region', filters.region);
  params.set('basis', filters.basis);
  params.set('attention', filters.onlyAttention ? '1' : '0');
  params.set('confidence', confidenceSelectionParam(filters.confidenceTier));
  if (analysis.unit === 'month') {
    params.delete('tccUnit');
  } else {
    params.set('tccUnit', analysis.unit);
  }
  if (analysis.period) {
    params.set(
      'tccPeriod',
      JSON.stringify({
        version: 1,
        ...analysis.period,
        priorFrom: analysis.priorRange?.dateFrom ?? null,
        priorTo: analysis.priorRange?.dateTo ?? null,
      }),
    );
  } else {
    params.delete('tccPeriod');
  }
  return `?${params}`;
}
